import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, MessageCircle } from "lucide-react";
import { NAV_LINKS, CONTACT } from "@/data/site";
import { LogoFull } from "@/components/Logo";

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <>
      <header
        data-testid="site-header"
        className="fixed inset-x-0 top-0 z-50 border-b border-white/10 bg-abyss/70 backdrop-blur-xl"
      >
        <div className="mx-auto flex h-20 max-w-[1440px] items-center justify-between px-6 lg:px-12">
          <Link to="/" data-testid="nav-logo-link" aria-label="Navis Odiseea — Acasă">
            <LogoFull compact />
          </Link>

          <nav className="hidden items-center gap-5 lg:flex" aria-label="Navigație principală">
            {NAV_LINKS.slice(1).map((link) => (
              <NavLink
                key={link.path}
                to={link.path}
                data-testid={link.testid}
                className={({ isActive }) =>
                  `gold-underline whitespace-nowrap text-[10px] font-normal uppercase tracking-[0.18em] transition-colors duration-300 ${
                    isActive ? "text-gold" : "text-ivory/80 hover:text-gold"
                  }`
                }
              >
                {link.name}
              </NavLink>
            ))}
            <a
              href={CONTACT.whatsapp}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-phone-cta"
              className="ml-2 flex shrink-0 items-center gap-2 border border-gold px-4 py-2.5 text-[10px] uppercase tracking-[0.18em] text-gold transition-colors duration-300 hover:bg-gold hover:text-abyss"
            >
              <MessageCircle className="h-3.5 w-3.5" />
              Rezervă
            </a>
          </nav>

          <button
            data-testid="mobile-menu-button"
            onClick={() => setOpen(!open)}
            className="text-ivory lg:hidden"
            aria-label={open ? "Închide meniul" : "Deschide meniul"}
          >
            {open ? <X className="h-7 w-7" /> : <Menu className="h-7 w-7" />}
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-40 flex flex-col justify-center bg-abyss/95 backdrop-blur-2xl lg:hidden"
          >
            <nav className="flex flex-col gap-2 px-8" aria-label="Meniu mobil">
              {NAV_LINKS.map((link, i) => (
                <motion.div
                  key={link.path}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 + i * 0.06, duration: 0.5 }}
                >
                  <NavLink
                    to={link.path}
                    data-testid={`mobile-${link.testid}`}
                    className={({ isActive }) =>
                      `block border-b border-white/10 py-4 font-serif text-3xl transition-colors ${
                        isActive ? "text-gold" : "text-ivory"
                      }`
                    }
                  >
                    {link.name}
                  </NavLink>
                </motion.div>
              ))}
              <motion.a
                href={CONTACT.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="mobile-phone-cta"
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6, duration: 0.5 }}
                className="mt-8 flex items-center justify-center gap-3 border border-gold py-4 text-sm uppercase tracking-[0.25em] text-gold"
              >
                <MessageCircle className="h-4 w-4" />
                {CONTACT.phone}
              </motion.a>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
